"use client";

import { Check, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Plan } from "@/types";

interface PricingFeature {
  text: string;
  included: boolean;
}

interface PricingCardProps {
  plan: Plan;
  name: string;
  price: number;
  description: string;
  features: PricingFeature[];
  highlight?: boolean;
  badge?: string;
  onSelect: (plan: Plan) => void;
  selected?: boolean;
}

export function PricingCard({
  plan,
  name,
  price,
  description,
  features,
  highlight,
  badge,
  onSelect,
  selected,
}: PricingCardProps) {
  return (
    <div className={cn("relative h-full", highlight && "lg:scale-[1.02]")}>
      <motion.div
        whileHover={{ y: -5 }}
        transition={{ type: "spring", stiffness: 280, damping: 22 }}
        onClick={() => onSelect(plan)}
        className={cn(
          "relative flex h-full flex-col cursor-pointer rounded-2xl p-5 sm:p-6",
          "transition-[box-shadow,border-color] duration-300",
          highlight
            ? "bg-gradient-to-b from-[#E11D48] to-[#9F1239] text-white shadow-[0_22px_50px_-18px_rgba(225,29,72,0.55)]"
            : "bg-white text-[#1F2937] border border-rose-100 shadow-[0_8px_22px_-12px_rgba(225,29,72,0.18)] hover:border-[#E11D48]/35 hover:shadow-[0_14px_30px_-12px_rgba(225,29,72,0.28)]",
          selected && !highlight && "border-[#E11D48] ring-2 ring-[#E11D48]/25",
          selected && highlight && "ring-[3px] ring-amber-300"
        )}
      >
        {highlight && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-2xl"
          >
            <span className="absolute -top-14 -right-10 h-36 w-36 rounded-full bg-white/10 blur-2xl" />
            <span className="absolute -bottom-10 -left-10 h-28 w-28 rounded-full bg-[#F59E0B]/20 blur-2xl" />
          </div>
        )}

        {badge && (
          <div className="absolute top-0 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2">
            <div className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] px-3.5 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-white shadow-lg shadow-amber-900/30 ring-2 ring-white/60">
              <Sparkles className="h-3 w-3" />
              {badge}
            </div>
          </div>
        )}

        <div className="relative z-10 flex flex-1 flex-col">
          <p
            className={cn(
              "mb-2 text-[10px] font-bold uppercase tracking-[0.18em]",
              highlight ? "text-rose-100/95" : "text-[#E11D48]"
            )}
          >
            {name}
          </p>

          <div className="flex items-baseline gap-1">
            <span
              className={cn(
                "text-sm font-bold",
                highlight ? "text-rose-100" : "text-gray-500"
              )}
            >
              R$
            </span>
            <span
              className={cn(
                "text-[38px] font-black leading-none tracking-tight",
                highlight ? "text-white" : "text-[#1F2937]"
              )}
            >
              {price.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
            <span
              className={cn(
                "text-[11px] font-medium",
                highlight ? "text-rose-100/80" : "text-gray-400"
              )}
            >
              / único
            </span>
          </div>

          <p
            className={cn(
              "mt-2 text-[13px] leading-snug",
              highlight ? "text-rose-50/95" : "text-gray-500"
            )}
          >
            {description}
          </p>

          <div
            className={cn(
              "my-4 h-px",
              highlight ? "bg-white/15" : "bg-rose-100"
            )}
          />

          <ul className="flex flex-1 flex-col gap-2">
            {features.map((f, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-[13px] leading-snug"
              >
                <span
                  className={cn(
                    "mt-px inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md",
                    f.included
                      ? highlight
                        ? "bg-white/20 text-white"
                        : "bg-[#FFE4E6] text-[#E11D48]"
                      : highlight
                        ? "bg-white/5 text-white/30"
                        : "bg-gray-100 text-gray-300"
                  )}
                >
                  <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
                </span>
                <span
                  className={cn(
                    f.included
                      ? highlight
                        ? "text-white"
                        : "text-[#1F2937]"
                      : highlight
                        ? "text-white/45 line-through"
                        : "text-gray-300 line-through"
                  )}
                >
                  {f.text}
                </span>
              </li>
            ))}
          </ul>

          <Button
            variant={highlight ? "gold" : selected ? "default" : "outline"}
            className="mt-5 w-full"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(plan);
            }}
          >
            {selected ? "Selecionado ✓" : "Escolher plano"}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
